import { useState, useEffect } from 'react';
import { reportEvent } from '../utils/telemetry';
import { getGuestPhotos, deleteGuestPhotos } from '../utils/guestPhotoStore';
import { uploadVisitBlob } from '../utils/visitPhotos';

// Fire the "a guest created their first content" signal exactly on the
// empty -> non-empty transition (issue #79 Phase 3). Aggregate, no personal data.
function reportFirstContent(wasEmpty) {
  if (wasEmpty && hasAnyGuestContent()) {
    reportEvent('guest_content_created');
  }
}

/**
 * Guest "save without an account" store (issue #13).
 *
 * Lets logged-out visitors favorite / wishlist restaurants with no backend and
 * no account. Saves live in localStorage as small display snapshots so the guest
 * can review them (see SavedPage) without any API calls. On login/registration,
 * mergeGuestSaves() pushes them to the backend and clears the local copy.
 *
 * Caveat (documented on the Saved page UI): localStorage isn't durable — it's
 * wiped by "clear site data"/privacy actions and never syncs across browsers or
 * devices. Creating an account is the only way to make saves permanent.
 */

const KEYS = {
  favorites: 'guest_favorites',
  wishlist: 'guest_wishlist',
  reviews: 'guest_reviews',
  visits: 'guest_visits',
};
// Backend path segment per kind (POST /users/<segment>/ with { restaurant_id }).
const ENDPOINTS = { favorites: 'favorites', wishlist: 'wishlist' };

function read(kind) {
  try {
    const raw = localStorage.getItem(KEYS[kind]);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error(`Failed to read guest ${kind} from localStorage:`, error);
    return [];
  }
}

function write(kind, items) {
  try {
    localStorage.setItem(KEYS[kind], JSON.stringify(items));
  } catch (error) {
    console.error(`Failed to write guest ${kind} to localStorage:`, error);
  }
  // Notify same-tab subscribers (the native `storage` event only fires in OTHER tabs).
  window.dispatchEvent(new CustomEvent(`guestsaves:${kind}`));
}

export function getGuestSaves(kind) {
  return read(kind);
}

export function isGuestSaved(kind, id, targetType = 'restaurant') {
  // A restaurant and a food can share an id, so match on id AND target type.
  // Legacy snapshots (pre-#102) have no target_type and are treated as restaurants.
  return read(kind).some((item) => item.id === id && (item.target_type || 'restaurant') === targetType);
}

/**
 * Add or remove a restaurant OR a food (#102) from a guest collection.
 * @param {'favorites'|'wishlist'} kind
 * @param {{id:number, target_type?:'restaurant'|'food', name:string, city?:string, state?:string, image?:string}} snapshot
 * @returns {boolean} the new saved state (true = now saved, false = removed)
 */
export function toggleGuestSave(kind, snapshot) {
  const wasEmpty = !hasAnyGuestContent();
  const items = read(kind);
  const snapType = snapshot.target_type || 'restaurant';
  const index = items.findIndex((item) => item.id === snapshot.id && (item.target_type || 'restaurant') === snapType);

  let isSaved;
  if (index >= 0) {
    items.splice(index, 1);
    isSaved = false;
  } else {
    items.unshift({ ...snapshot, saved_at: new Date().toISOString() });
    isSaved = true;
  }

  write(kind, items);
  if (isSaved) reportFirstContent(wasEmpty);
  return isSaved;
}

export function clearGuestSaves(kind) {
  if (kind) {
    try {
      localStorage.removeItem(KEYS[kind]);
    } catch (error) {
      console.error(`Failed to clear guest ${kind}:`, error);
    }
    window.dispatchEvent(new CustomEvent(`guestsaves:${kind}`));
  } else {
    Object.keys(KEYS).forEach((k) => clearGuestSaves(k));
  }
}

export function hasAnyGuestSaves() {
  return read('favorites').length > 0 || read('wishlist').length > 0;
}

/**
 * Fold all guest saves into the authenticated account, then clear the local
 * copy. Reuses the existing single-add endpoints (counts are tiny). A 400/409
 * means the restaurant is already saved server-side — treated as success.
 * Items that fail to send (e.g. network error) are kept locally for a later retry.
 *
 * @returns {Promise<{favorites:number, wishlist:number}>} count merged per kind
 */
export async function mergeGuestSaves(apiBaseUrl, token) {
  const merged = { favorites: 0, wishlist: 0 };

  for (const kind of Object.keys(ENDPOINTS)) {
    const items = read(kind);
    if (items.length === 0) continue;

    const remaining = [];
    for (const item of items) {
      try {
        const response = await fetch(`${apiBaseUrl}/users/${ENDPOINTS[kind]}/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          // #102: a food save posts food_id; a restaurant save posts restaurant_id.
          body: JSON.stringify(item.target_type === 'food' ? { food_id: item.id } : { restaurant_id: item.id }),
        });
        // ok = newly added; 400/409 = already saved server-side — both are fine.
        if (response.ok || response.status === 400 || response.status === 409) {
          merged[kind] += 1;
        } else {
          remaining.push(item);
        }
      } catch (error) {
        console.error(`Failed to merge guest ${kind} item ${item.id}:`, error);
        remaining.push(item);
      }
    }

    write(kind, remaining);
  }

  return merged;
}

/* ------------------------------------------------------------------ *
 * Draft reviews & visits (issue #13)
 *
 * Unlike favorites/wishlist (idempotent toggles keyed by restaurant_id),
 * reviews and visits are append-only DRAFTS authored while logged out. They stay
 * 100% local — nothing is published — until the guest creates or logs into an
 * account, at which point mergeGuestDrafts() posts them for real:
 *   - visits always POST (no uniqueness constraint),
 *   - reviews POST too, but the backend allows only one review per target, so a
 *     collision with a pre-existing account review is surfaced as a conflict for
 *     the user to resolve (keep theirs vs use the draft) — see
 *     resolveGuestReviewConflict.
 * ------------------------------------------------------------------ */

let draftSeq = 0;
function makeDraftId() {
  draftSeq += 1;
  return `${Date.now()}-${draftSeq}`;
}

/**
 * Append a local draft review or visit.
 * @param {'reviews'|'visits'} kind
 * @param {object} draft request payload fields + a `target` snapshot ({id,name})
 *   for display on the Saved page
 * @returns {object} the stored draft (with draft_id + saved_at)
 */
export function addGuestDraft(kind, draft) {
  const wasEmpty = !hasAnyGuestContent();
  const items = read(kind);
  const stored = { ...draft, draft_id: makeDraftId(), saved_at: new Date().toISOString() };
  items.unshift(stored);
  write(kind, items);
  reportFirstContent(wasEmpty);
  return stored;
}

export function getGuestDrafts(kind) {
  return read(kind);
}

export function removeGuestDraft(kind, draftId) {
  write(kind, read(kind).filter((d) => d.draft_id !== draftId));
  // #107: discarding a visit draft also drops any photos stashed for it.
  if (kind === 'visits') deleteGuestPhotos(draftId);
}

/** True if the guest has saved anything at all (saves OR drafts). */
export function hasAnyGuestContent() {
  return (
    read('favorites').length > 0 ||
    read('wishlist').length > 0 ||
    read('reviews').length > 0 ||
    read('visits').length > 0
  );
}

/**
 * Like hasAnyGuestContent(), but leaves out quarantined drafts (#227). They
 * stay in storage until the user acts, so counting them would make every later
 * sign-in look like a guest converting.
 */
export function hasPendingGuestContent() {
  return (
    read('favorites').length > 0 ||
    read('wishlist').length > 0 ||
    read('reviews').some((d) => !d.failed) ||
    read('visits').some((d) => !d.failed)
  );
}

function toReviewPayload(draft) {
  return {
    rating: draft.rating,
    title: draft.title || null,
    review_text: draft.review_text || null,
    restaurant_id: draft.restaurant_id || null,
    food_id: draft.food_id || null,
    menu_item_id: draft.menu_item_id || null,
  };
}

// Only the mutable fields go in a PUT update (target ids are fixed).
function toReviewUpdatePayload(draft) {
  return {
    rating: draft.rating,
    title: draft.title || null,
    review_text: draft.review_text || null,
  };
}

function toVisitPayload(draft) {
  const payload = {
    restaurant_id: draft.restaurant_id,
    location_id: draft.location_id || null,
    visit_date: draft.visit_date,
    meal_type: draft.meal_type || null,
    rating: draft.rating,
    notes: draft.notes || null,
    create_public_review: !!draft.create_public_review,
  };
  // #110: newer drafts carry the full dish list; older drafts (pre-#110) still
  // have the singular fields. Send whichever this draft has — the backend
  // mirrors dishes[0] onto the singular columns either way.
  if (Array.isArray(draft.dishes)) {
    payload.dishes = draft.dishes;
  } else {
    payload.food_id = draft.food_id || null;       // #102 rung-1 (legacy draft)
    payload.menu_item_id = draft.menu_item_id || null;
    payload.dish_name = draft.dish_name || null;   // #102 rung-2 off-menu (legacy draft)
  }
  return payload;
}

// Find the current user's existing review for the draft's target, so a conflict
// can be resolved with a PUT. Returns the review id or null.
async function findExistingReviewId(apiBaseUrl, token, currentUserId, draft) {
  if (!currentUserId) return null;
  const params = new URLSearchParams({ user_id: String(currentUserId) });
  if (draft.restaurant_id) params.set('restaurant_id', String(draft.restaurant_id));
  if (draft.food_id) params.set('food_id', String(draft.food_id));
  if (draft.menu_item_id) params.set('menu_item_id', String(draft.menu_item_id));
  try {
    const res = await fetch(`${apiBaseUrl}/reviews/?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const list = await res.json();
    const mine = Array.isArray(list) ? list.find((r) => r.user_id === currentUserId) : null;
    return mine ? mine.id : null;
  } catch (error) {
    console.error('Failed to look up existing review for conflict:', error);
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Drafts that can never post (#227)
 *
 * A failed draft used to go back in the queue whatever the failure, so a draft
 * the server rejects as invalid was re-POSTed at every sign-in, for ever, with
 * nothing shown. Failures are two opposite kinds:
 *   - transient (network, 5xx, 408, 429, and 401/403, which are about the token,
 *     not the draft): the same payload can succeed later, so retry;
 *   - permanent (any other 4xx, e.g. 422): it never will, so stop.
 * A permanently failed draft is QUARANTINED, not deleted: it stays in storage
 * marked `failed`, is never retried automatically, and is surfaced at every
 * sign-in until the user tries again or discards it. MAX_MERGE_ATTEMPTS bounds
 * the transient kind too, so no failure mode can loop unbounded. Drafts don't
 * expire (decided 2026-10-23): that would delete content silently.
 * ------------------------------------------------------------------ */

export const MAX_MERGE_ATTEMPTS = 5;

/** True when an HTTP status means this payload will never succeed. */
export function isPermanentFailure(status) {
  if (status == null) return false; // network error
  if ([401, 403, 408, 429].includes(status)) return false;
  return status >= 400 && status < 500;
}

/** The draft after one more failed attempt, quarantined if it can't post. */
export function recordMergeFailure(draft, status, detail) {
  const attempts = (draft.merge_attempts || 0) + 1;
  return {
    ...draft,
    merge_attempts: attempts,
    last_error: { status: status ?? null, detail: detail || null, at: new Date().toISOString() },
    failed: isPermanentFailure(status) || attempts >= MAX_MERGE_ATTEMPTS,
  };
}

// The server's reason, as text a person can read. FastAPI sends a string, or
// for a 422 a list of {loc, msg}.
async function errorDetail(res) {
  try {
    const body = await res.json();
    if (typeof body.detail === 'string') return body.detail;
    if (Array.isArray(body.detail)) {
      return body.detail.map((e) => [(e.loc || []).slice(1).join('.'), e.msg].filter(Boolean).join(': ')).join('; ');
    }
  } catch { /* no JSON body */ }
  return res.statusText || null;
}

/**
 * Post all guest visit + review drafts to the account, clearing what succeeds.
 * Visits always post. A review 400 (one-per-target) is a CONFLICT, not a
 * failure: the draft is kept and returned so the UI can ask the user how to
 * resolve it. Transient failures keep the draft for a later retry; permanent
 * ones quarantine it (#227). Quarantined drafts are skipped, and returned in
 * `failed` every time so the user keeps seeing them until they act.
 *
 * @param {string} apiBaseUrl
 * @param {string} token
 * @param {number} currentUserId  used to locate the existing review on conflict
 * @returns {Promise<{visits:number, reviews:number,
 *   conflicts:Array<{draft:object, existingReviewId:?number}>,
 *   failed:Array<{kind:'visits'|'reviews', draft:object}>}>}
 */
export async function mergeGuestDrafts(apiBaseUrl, token, currentUserId) {
  const result = { visits: 0, reviews: 0, conflicts: [], failed: [] };
  // Keep a failed draft, and surface it if it's now quarantined.
  const keepFailed = (kind, remaining, draft, status, detail) => {
    const recorded = recordMergeFailure(draft, status, detail);
    remaining.push(recorded);
    if (recorded.failed) result.failed.push({ kind, draft: recorded });
  };
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const visits = read('visits');
  if (visits.length) {
    const remaining = [];
    for (const draft of visits) {
      if (draft.failed) {
        remaining.push(draft);
        result.failed.push({ kind: 'visits', draft });
        continue;
      }
      try {
        const res = await fetch(`${apiBaseUrl}/api/v1/visits/`, {
          method: 'POST', headers: authHeaders, body: JSON.stringify(toVisitPayload(draft)),
        });
        if (res.ok) {
          result.visits += 1;
          // #107: upload any photos the guest stashed for this draft to the new
          // visit, then drop them. Best-effort — a photo failure doesn't unwind
          // the visit (which is already posted).
          try {
            const visit = await res.json();
            // #115: re-attribute each stashed photo to its dish. The stash carries
            // a `position` (dish index in the payload we just posted, null =
            // "other"); map it to the dish's server id from the create response.
            const posToId = {};
            (visit.dishes || []).forEach((d) => { posToId[d.position] = d.id; });
            const photos = await getGuestPhotos(draft.draft_id);
            let uploadedDishPhoto = 0;
            for (const { position, blob } of photos) {
              const vdid = position != null ? posToId[position] : null;
              try {
                await uploadVisitBlob(apiBaseUrl, token, visit.id, blob, vdid);
                if (vdid != null) uploadedDishPhoto += 1;
              } catch { /* skip this photo */ }
            }
            await deleteGuestPhotos(draft.draft_id);
            // #114/#115: photos upload as private/na; for a draft shared as a public
            // review, opt the DISH photos in so they show on it (parity with the
            // signed-in path). dish_photos_only keeps "other" photos private. The
            // guest draft doesn't persist the include-photos choice, so default
            // share-on — the guest already chose to make the visit public.
            if (uploadedDishPhoto > 0 && draft.create_public_review) {
              try {
                await fetch(`${apiBaseUrl}/api/v1/visits/${visit.id}/share-photos?share=true&dish_photos_only=true`, {
                  method: 'POST', headers: authHeaders,
                });
              } catch (shareErr) {
                console.error('Failed to share guest visit photos on merge:', shareErr);
              }
            }
          } catch (photoErr) {
            console.error('Failed to upload guest visit photos on merge:', photoErr);
          }
        } else {
          keepFailed('visits', remaining, draft, res.status, await errorDetail(res));
        }
      } catch (error) {
        console.error('Failed to merge guest visit draft:', error);
        keepFailed('visits', remaining, draft, null, null);
      }
    }
    write('visits', remaining);
  }

  const reviews = read('reviews');
  if (reviews.length) {
    const remaining = [];
    for (const draft of reviews) {
      if (draft.failed) {
        remaining.push(draft);
        result.failed.push({ kind: 'reviews', draft });
        continue;
      }
      try {
        const res = await fetch(`${apiBaseUrl}/reviews/`, {
          method: 'POST', headers: authHeaders, body: JSON.stringify(toReviewPayload(draft)),
        });
        if (res.ok) {
          result.reviews += 1;
        } else if (res.status === 400) {
          // One-per-target collision — surface for the user to resolve. Keep the draft.
          const existingReviewId = await findExistingReviewId(apiBaseUrl, token, currentUserId, draft);
          result.conflicts.push({ draft, existingReviewId });
          remaining.push(draft);
        } else {
          keepFailed('reviews', remaining, draft, res.status, await errorDetail(res));
        }
      } catch (error) {
        console.error('Failed to merge guest review draft:', error);
        keepFailed('reviews', remaining, draft, null, null);
      }
    }
    write('reviews', remaining);
  }

  return result;
}

/**
 * Resolve one guest-review merge conflict.
 * @param {{draft:object, existingReviewId:?number}} conflict
 * @param {'keep'|'replace'} choice  keep the account's review, or PUT the draft over it
 * @returns {Promise<boolean>} true once resolved (draft removed)
 */
export async function resolveGuestReviewConflict(apiBaseUrl, token, conflict, choice) {
  const { draft, existingReviewId } = conflict;

  if (choice === 'keep') {
    removeGuestDraft('reviews', draft.draft_id);
    return true;
  }

  if (choice === 'replace') {
    if (!existingReviewId) return false; // couldn't locate it; leave the draft for retry
    try {
      const res = await fetch(`${apiBaseUrl}/reviews/${existingReviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(toReviewUpdatePayload(draft)),
      });
      if (res.ok) {
        removeGuestDraft('reviews', draft.draft_id);
        return true;
      }
    } catch (error) {
      console.error('Failed to replace review on conflict:', error);
    }
    return false;
  }

  return false;
}

/**
 * "Try again" on a quarantined draft (#227): clear its failure record, so the
 * next mergeGuestDrafts() posts it as if new. Discard is removeGuestDraft().
 */
export function unquarantineGuestDraft(kind, draftId) {
  write(kind, read(kind).map((d) => {
    if (d.draft_id !== draftId) return d;
    const { failed, merge_attempts, last_error, ...rest } = d;
    return rest;
  }));
}

/**
 * Reactive list of a guest collection. Re-renders on same-tab toggles
 * (CustomEvent) and cross-tab changes (native storage event).
 */
export function useGuestSaves(kind) {
  const [items, setItems] = useState(() => read(kind));

  useEffect(() => {
    const refresh = () => setItems(read(kind));
    refresh();
    window.addEventListener(`guestsaves:${kind}`, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(`guestsaves:${kind}`, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [kind]);

  return items;
}
